import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api-client";
import type { Shape } from "./model";
function dataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(Error("Could not read image"));
    reader.readAsDataURL(blob);
  });
}
export function useBoardImages(
  shapes: Shape[],
  endpoint: string,
  token?: string,
) {
  const [images, setImages] = useState<Record<string, string>>({}),
    [failed, setFailed] = useState(false),
    [attempt, setAttempt] = useState(0);
  const cache = useRef<{ scope: string; images: Record<string, string> }>({
      scope: "",
      images: {},
    }),
    scope = endpoint + "|" + (token ?? "");
  const ids = Array.from(
    new Set(shapes.flatMap((s) => (s.imageId ? [s.imageId] : []))),
  )
    .sort()
    .join("|");
  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();
    if (cache.current.scope !== scope) {
      cache.current = { scope, images: {} };
      setImages({});
    }
    const activeIds = new Set(ids.split("|"));
    cache.current.images = Object.fromEntries(
      Object.entries(cache.current.images).filter(([id]) => activeIds.has(id)),
    );
    setImages({ ...cache.current.images });
    setFailed(false);
    void (async () => {
      for (const id of ids.split("|").filter(Boolean)) {
        if (cancelled) return;
        if (cache.current.images[id]) continue;
        try {
          const response = await api.get(
            `${endpoint}/images/${encodeURIComponent(id)}`,
            {
              responseType: "blob",
              params: token ? { t: token } : undefined,
              signal: abort.signal,
              timeout: 15000,
            },
          );
          const url = await dataUrl(response.data);
          if (cancelled) return;
          cache.current.images[id] = url;
          setImages({ ...cache.current.images });
        } catch {
          if (!cancelled) setFailed(true);
        }
        await new Promise((r) => setTimeout(r, 220));
      }
    })();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [ids, scope, attempt]);
  return {
    images: cache.current.scope === scope ? images : {},
    failed,
    retry: () => setAttempt((x) => x + 1),
  };
}
export async function uploadBoardImage(file: File, endpoint: string) {
  if (
    !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
    file.size > 12 * 1024 * 1024
  )
    throw Error("Choose a JPEG, PNG or WebP image up to 12 MB.");
  const source = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = source;
    await img.decode();
    const scale = Math.min(
        1,
        1600 / Math.max(img.naturalWidth, img.naturalHeight),
      ),
      canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw Error("Image processing is unavailable");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(Error("Image processing failed"))),
        "image/jpeg",
        0.85,
      ),
    );
    const form = new FormData();
    form.append("file", blob, "board-image.jpg");
    return (
      await api.post<{ imageId: string; width: number; height: number }>(
        endpoint + "/images",
        form,
        { timeout: 60000 },
      )
    ).data;
  } finally {
    URL.revokeObjectURL(source);
  }
}
export async function exportCanvas(svg: SVGSVGElement, format: "svg" | "png") {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.querySelectorAll("[data-editor]").forEach((el) => el.remove());
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", "1600");
  clone.setAttribute("height", "960");
  clone.removeAttribute("style");
  clone.removeAttribute("class");
  const blob = new Blob([new XMLSerializer().serializeToString(clone)], {
    type: "image/svg+xml",
  });
  let url = URL.createObjectURL(blob);
  try {
    if (format === "png") {
      const img = new Image();
      img.src = url;
      await img.decode();
      const canvas = document.createElement("canvas");
      canvas.width = 1600;
      canvas.height = 960;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw Error("PNG export is unavailable");
      ctx.drawImage(img, 0, 0);
      const png = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(Error("PNG export failed"))),
          "image/png",
        ),
      );
      URL.revokeObjectURL(url);
      url = URL.createObjectURL(png);
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = `interactive-board.${format}`;
    link.click();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
